import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Search, Sparkles } from "lucide-react";
import QuickEntry from "@/components/quick/QuickEntry";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import Brand from "@/components/common/Brand";
import { navigation } from "./navigation";

export default function Header({ title, onRefresh, isRefreshing = false }) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const results = navigation.filter((item) =>
    `${item.label} ${item.group}`.toLowerCase().includes(search.toLowerCase()),
  );
  useEffect(() => {
    function onKey(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "j") {
        event.preventDefault();
        setQuickOpen((value) => !value);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return (
    <header className="owner-header">
      <span className="owner-header-brand">
        <Brand to="/dashboard" compact />
      </span>
      <span className="owner-header-title">{title}</span>
      <div className="owner-header-actions">
        <Button size="sm" className="mr-2 gap-1.5" onClick={() => setQuickOpen(true)} aria-label="Quick add">
          <Sparkles size={15} />
          <span className="hidden sm:inline">Quick add</span>
        </Button>
        <button
          className="workspace-search"
          onClick={() => {
            setSearch("");
            setOpen(true);
          }}
          aria-label="Search workspace"
        >
          <Search size={16} />
          <span>Go to…</span>
          <kbd>⌘ K</kbd>
        </button>
        {onRefresh && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh data"
          >
            <RefreshCw
              size={16}
              className={isRefreshing ? "animate-spin" : ""}
            />
          </Button>
        )}
        <ThemeToggle />
        <Button
          size="icon"
          variant="ghost"
          onClick={logout}
          aria-label="Log out"
        >
          <LogOut size={16} />
        </Button>
      </div>
      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogTitle>What happened?</DialogTitle>
          <DialogDescription>
            Type it like a note — house, amount, contractor or tenant. Check the preview, then save. Tip: ⌘/Ctrl + J opens this anywhere.
          </DialogDescription>
          <QuickEntry autoFocus onDone={() => setQuickOpen(false)} />
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogTitle>Go to your workspace</DialogTitle>
          <DialogDescription>
            Find a page. Tip: press Command or Control + K anywhere.
          </DialogDescription>
          <input
            className="owner-input"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ledger, houses, contacts…"
            aria-label="Search workspace pages"
            onKeyDown={(e) => {
              if (e.key === "Enter" && results[0]) {
                navigate(results[0].path);
                setOpen(false);
              }
            }}
          />
          <div className="search-results">
            {results.map(({ path, label, icon: Icon }) => (
              <Link key={path} to={path} onClick={() => setOpen(false)}>
                <Icon size={17} />
                {label}
              </Link>
            ))}
            {results.length === 0 && (
              <p>No pages match. Try “ledger” or “houses”.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
